<template>
  <LineChart
    :request-status="requestStatus"
    :series="series"
    :title="title"
    :item-id="itemId"
    class="make-a-copy"
    :card-options="cardOptions"
    v-bind="$attrs"
    @reload="getData"
  />
</template>

<script>
import LineChart from '@/components/cards/models/LineChart';
import { mapActions } from 'vuex';
import sentimentModel from '@/mixins/sentiment-model';
import cardsModel from '@/mixins/cards-model';
import EventBus from '@/utils/event-bus';
import { POST_FIELDS, OPERATION } from '@/services/charts-service';

export default {
  name: 'SentimentTimeSeries',
  components: {
    LineChart,
  },
  mixins: [
    sentimentModel,
    cardsModel,
  ],
  props: {
    title: {
      type: String,
      required: true,
    },
    itemId: {
      type: [String, Number],
      required: true,
    },
    operation: {
      type: String,
      default: OPERATION.COUNT,
    },
    dataType: {
      type: [String, null],
      default: 'absolute',
    },
    ownerPosts: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      requestStatus: 'loading',
      rawSeries: [],
      totalPosts: 0,
      seriesNames: [
        'Positivo',
        'Negativo',
        'Neutro',
      ],
      cardOptions: {
        chart: {
          type: 'areaspline',
        },
        legend: {
          enabled: true,
        },
        plotOptions: {
          areaspline: {
            stacking: this.dataType === 'absolute' ? 'normal' : null,
            lineWidth: 0.5,
            lineColor: 'black',
            marker: {
              enabled: false,
            },
          },
        },
        tooltip: {
          pointFormat: this.dataType === 'absolute'
            ? '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y}</b><br/>'
            : '<span style="color:{point.color}">\u25CF</span> {series.name}: <b>{point.y:.1f}% ({point.absoluteY})</b><br/>',
        },
        yAxis: {
          labels: {
            format: this.dataType === 'absolute' ? '{value}' : '{value}%',
          },
        },
        xAxis: {},
      },
    };
  },
  computed: {
    series() {
      return this.dataType === 'absolute'
        ? this.seriesAbsolute
        : this.seriesPercent;
    },
    seriesAbsolute() {
      if (this.rawSeries && this.rawSeries.length) {
        return this.rawSeries.map((rawSerie) => {
          rawSerie.data = rawSerie.data.map((val, index) => [this.categories[index], val || 0]);
          return {
            color: this.getSentimentColor(rawSerie.name),
            ...rawSerie,
            name: this.translateSentimentName(rawSerie.name),
          };
        });
      }
      return [];
    },
    seriesPercent() {
      if (this.rawSeries && this.rawSeries.length) {
        const absolutePointsArray = this.calculateSumSeriesPoints(this.rawSeries);
        return this.rawSeries.map((rawSerie) => {
          const { data, name } = rawSerie;
          const dataWithAbsoluteValueAndPercent = data.map((value, position) =>
            ({
              x: this.categories[position],
              absoluteY: value,
              y: this.calculateSeriesPercent(value, absolutePointsArray[position]),
            })
          );
          const color = this.getSentimentColor(rawSerie.name);
          return {
            color,
            lineColor: color,
            lineWidth: 2,
            fillOpacity: 0.1,
            ...rawSerie,
            name: this.translateSentimentName(name),
            data: dataWithAbsoluteValueAndPercent,
          };
        });
      }
      return [];
    },
  },
  created() {
    this.getData();
  },
  methods: {
    ...mapActions('cards', [
      'getChartsService',
    ]),
    translateSentimentName(name) {
      const map = {
        'Positivo': this.$t('mixins.sentimentModel.positive'),
        'Negativo': this.$t('mixins.sentimentModel.negative'),
        'Neutro': this.$t('mixins.sentimentModel.neutral'),
        'Não Classificado': this.$t('mixins.sentimentModel.notClassified'),
      };
      return map[name] || name;
    },
    calculateSumSeriesPoints(rawSeries) {
      return rawSeries.reduce((accumulator, { data }) =>
        data.map((item, index) =>
          item + (accumulator[index] || 0)), []);
    },
    calculateSeriesPercent(value, sumTotalPoint) {
      return value ? (value / sumTotalPoint) * 100 : 0;
    },
    orderDataArrayAsc(data) {
      return data.sort();
    },
    setXAxisCategories(categories) {
      const rawCategories = this.orderDataArrayAsc([...categories]);
      const formattedCategories = this.formattedCategoriesForQuarter(rawCategories);

      this.categories = formattedCategories;
    },
    filterByPointName({ name }) {
      return this.seriesNames.includes(name);
    },
    getPolarityFilter(num) {
      return {
        filter: {
          polarities: {
            value: [num],
          },
        },
      };
    },
    getData(options) {
      if (!options?.silentRefresh) {
        this.requestStatus = 'loading';
      }

      this.getChartsService({
        itemId: this.itemId,
        method: 'getTimeSeries',
        params: {
          grouping: [POST_FIELDS.POLARITY],
          operators: this.operation,
          datePortion: this.getDatePortion(options),
        },
      })
        .then((response) => {
          this.requestStatus = 'success';

          if (options?.silentRefresh) {
            EventBus.$emit('updated', { itemId: this.itemId });
          }

          const { points, axes: [axi] } = this.normalizeSentimentResponseFromChartService(response);
          const categoriesSerie = axi?.values || [];
          this.setXAxisCategories(categoriesSerie);
          this.rawSeries = points.filter(this.filterByPointName);
        })
        .catch(() => {
          this.requestStatus = 'error';
        });
    },
  },
};
</script>

